interface IOffset {
    x: number;
    y: number;
}


module TableFreezePaneUtility {
    var fixedHeadersEnabled: boolean = false;
    var fixedHeaderTables: Array<FixedHeaderTable> = [];

    export function enableFreezePanes(className: string) {
        //Find all tables that match the 
        var tables = document.querySelectorAll(`table.${className}`);

        for (var i = 0; i < tables.length; i++) {
            var table = tables[i] as HTMLTableElement;
            fixedHeaderTables.push(new FixedHeaderTable(table));
        }

        if (fixedHeadersEnabled) return;
        fixedHeadersEnabled = true;

        document.addEventListener("scroll", fixedHeaderScrollHandler);
        window.addEventListener("resize", resizeHandler);
    }

    function fixedHeaderScrollHandler() {
        for (let table of fixedHeaderTables) {
            table.onWindowScroll();
        }
    }

    function resizeHandler() {
        for (let table of fixedHeaderTables) {
            table.onResize();
        }
    }
}


class FixedHeaderTable {
    /**
     * The HTML Table element that will get the fixed header
     */
    table: HTMLTableElement;

    /**
     * The original thead element in the table
     */
    head: HTMLHeadElement;

    /**
     * The copy of the original thead element that will shown when fixing the header
     */
    headCopy: HTMLHeadElement;

    /**
     * Array of cells that are included in defined freeze columns
     */
    freezeColumnCells: Array<HTMLTableCellElement> = [];

    /**
     * The copy of the original cells that are copied to they can be frozen
     */
    freezeColumnCellCopies: Array<HTMLTableCellElement> = [];

    /**
     * If the table is in a table-responsive parent container so it can scroll x and y
     */
    private isResponsive: boolean = false;

    /**
     * If the table is able to have a frozen header
     */
    private canFreeze: boolean = true;

    /**
     * If the table currently has its header frozen
     */
    private isHeadFrozen: boolean = false;

    /**
     * If the table's height has been altered to fit the viewport
     */
    private isHeightConstrained: boolean = false;

    /**
     * If the header needs to be frozen relative to its container rather than the viewport
     * [And use sticky instead of fixed positioning]
     */
    private needsSticky: boolean = false;

    /**
     * If the table's container initially scrolls vertically
     */
    private initiallyScrollsY: boolean = false;

    /**
     * If the table also needs frozen column(s)
     */
    private hasFreezeColumn: boolean = false;


    /**
     * Constructs the class that will handle fixing a header for a specific html table element
     * @param table The table element to get the fixed header(s)
     */
    constructor(table: HTMLTableElement) {
        this.table = table;
        this.head = table.querySelector("thead");

        this.isResponsive = this.table.parentElement.classList.contains("table-responsive");

        this.initiallyScrollsY = this.canScrollY();

        if (this.isResponsive) {
            this.table.style.position = "relative";
        }

        this.initFreezePaneColumn();

        //Create the copy
        var c = <HTMLHeadElement>this.head.cloneNode(true);
        c.hidden = true;
        c.classList.remove("freeze-pane");
        c.classList.add("freeze-pane-copy");

        this.headCopy = c;
        this.head.after(this.headCopy);

        //Call the onResize handler to do initial determination of capabilities, sizing, etc
        this.onResize();
        this.onWindowScroll();
        this.onTableScroll();

        var self = this;
        if (this.isResponsive) {
            this.table.parentElement.addEventListener("scroll", () => {
                self.onTableScroll();
            });
        }
    }


    /** Event callback method used to handle changes in browser window size */
    onResize(): void {
        var scrollsEither = this.canScroll();
        var scrollsX = this.canScrollX();

        //We are able to fix the header if there is no scrolling, or if the scrolling is handled properly by the parent table-responsive class
        this.canFreeze = !scrollsEither || this.isResponsive;
        if (!this.canFreeze) {
            this.unfreezeHeader();
            return;
        }

        //We'll need to affix the header to the parent container if it scrolls
        if (this.canSticky()) {
            this.setSticky();
        } else {
            this.setNotSticky();
        }


        //If a sticky fix is needed, its also best to have it height constrained
        //But only modified the height if not initially set. If initially set, it will probably already scroll, so that's how we'll check
        if (!this.initiallyScrollsY && this.needsSticky) {
            this.constrainHeight();
        } else if (!this.initiallyScrollsY) {
            this.unconstrainHeight();
        }

        
        //If the header is currently fixed, fix the widths to match the table
        if (!this.isHeadFrozen) return;
        this.matchColumnWidths();
    }


    /** Event callback method used to fix and unfix the header based on the browser scroll position */
    onWindowScroll(): void {
        if (!this.canFreeze) {
            this.unfreezeHeader();
            return;
        }

        //Only used for non-sticky positioning fixing
        if (this.needsSticky) return;
        
        var vpo = this.getViewportRelativeOffset(this.table);
        var bottom = vpo.y + this.table.clientHeight;
        var cutoff = bottom - this.head.clientHeight;

        if (vpo.y < 0 && cutoff > 0) {
            this.freezeHeader();
        } else {
            this.unfreezeHeader();
        }
    }

    /** Event callback method for when a responsive table's container is scrolled */
    onTableScroll(): void {
        if (!this.isResponsive) return;

        if (!this.canFreeze) {
            this.unfreezeHeader();
            return;
        }

        //Only used for sticky positioning based fixing
        if (!this.needsSticky) return;

        if (this.table.parentElement.scrollTop > 0) {
            this.freezeHeader();
        } else {
            this.unfreezeHeader();
        }
    }


    /** Do initial discovery & config of freeze pane columns, even if table does not scroll currently */
    private initFreezePaneColumn(): void {
        var headerRows = this.head.querySelectorAll("tr");
        if (!headerRows.length) {
            this.hasFreezeColumn = false;
            return;
        };

        //Get columns from last row
        var headerColumns = headerRows[headerRows.length - 1].querySelectorAll("th");
        var columnIndex = -1;

        for (var i = 0; i < headerColumns.length; i++) {
            if (headerColumns[i].classList.contains("freeze-pane")) {
                //Find the last freeze pane
                //TODO: potentially add support for freeze panes that will push the previous freeze pane out, or will stack
                columnIndex = i;
            }
        }

        this.hasFreezeColumn = columnIndex >= 0;
        if (!this.hasFreezeColumn) return;

        var maxWidth = 0;

        var rows = this.table.querySelectorAll("tbody > tr");
        for (var r = 0; r < rows.length; r++) {
            let row = rows[r] as HTMLTableRowElement;
            var cell = row.children[columnIndex] as HTMLTableCellElement;

            maxWidth = Math.max(maxWidth, cell.clientWidth);
            cell.classList.add("freeze-pane");

            this.freezeColumnCells.push(cell);

            //var rowStyle = window.getComputedStyle(row);
            //var bgColor = rowStyle.backgroundColor;
            //var border = rowStyle.border;
            //cell.style.backgroundColor = rowStyle.backgroundColor;
        }

        for (let cell of this.freezeColumnCells) {
            cell.width = `${maxWidth}px`;
        }
    }


    private canSticky(): boolean {
        //Requires to be in a .table-responsive container
        if (!this.isResponsive) return false;

        //Bootstrap won't scroll vertically in mobile, so don't use sticky in mobile
        //Bootstrap uses < 768 as the determination, so match on the same media query they do
        //TODO: Re-enable this. Would require using fixed header with sticky columns
        /*if (window.matchMedia('(max-width:767px)')) {
            return false;
        }*/

        //If the table needs to scroll horizontally, then we can use sticky 
        if (this.canScrollX()) {
            return true;
        }

        //If the table is height limited from the start (from another rule limiting its height)
        //And its current scrolls in either direction, we can also use sticky
        if (this.canScroll() && this.initiallyScrollsY) {
            return true;
        }

        return false;
    }



    /** Set fixed method to use sticky positioning */
    private setSticky(): void {
        //Do nothing if already set
        if (this.needsSticky) return;


        this.needsSticky = true;
        //this.headCopy.style.position = "sticky";
        //this.headCopy.classList.add("sticky-header");
        this.table.classList.add("table-freeze-panes-sticky");

        this.table.parentElement.classList.add("table-freeze-panes-sticky-container");

        this.onTableScroll();
    }

    /** Unset fixed method from using sticky (go back to fixed positioning) */
    private setNotSticky(): void {
        //Do nothing if already set
        if (!this.needsSticky) return;

        this.needsSticky = false;
        this.table.classList.remove("table-freeze-panes-sticky");
        this.table.parentElement.classList.remove("table-freeze-panes-sticky-container");
    }


    /**
     * Makes the table fit within the viewport so it can be scrolled properly
     */
    private constrainHeight(): void {
        this.isHeightConstrained = true;

        //Get the viewport height
        var viewPortHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

        //Calculate the table height, minus some padding for the portlet, in case it exists
        //Down to a minimum table height in case the browser is extra tiny for some reason
        var height = Math.max(viewPortHeight - 200, 200);

        this.table.parentElement.style.maxHeight = `${height}px`;

        //Trigger the on scroll event, so fixed headers can get cleaned up if they were affected
        this.onTableScroll();
    }

    /**
     * Removes the previous constraining to the viewport height
     */
    private unconstrainHeight(): void {
        if (!this.isHeightConstrained) return;

        this.isHeightConstrained = false;
        this.table.parentElement.style.maxHeight = null;

        //Trigger the on scroll event, so fixed headers can get cleaned up if they were affected
        this.onWindowScroll();
    }


    
    /**
     * Freezes the header to the top of the viewport or container
     */
    private freezeHeader(): void {
        if (this.isHeadFrozen) return;
        this.isHeadFrozen = true;


        this.table.classList.add("stuck-header");
        this.head.style.visibility = "hidden";
        this.headCopy.hidden = false;

        if (this.needsSticky) {
            this.head.hidden = true;
        }

        this.matchColumnWidths();
    }

    /**
     * Unfreezes the header from the top of the viewport or container
     */
    private unfreezeHeader(): void {
        if (!this.isHeadFrozen) return;
        this.isHeadFrozen = false;

        this.table.classList.remove("stuck-header");
        this.head.hidden = false;
        this.head.style.visibility = null;
        this.headCopy.hidden = true;
    }

    /** Determines if the table's container can scroll in either direction */
    private canScroll(): boolean {
        return this.canScrollY() || this.canScrollX();
    }

    /** Determines if the table's container can scroll vertically */
    private canScrollY(): boolean {
        var e = this.table.parentElement;
        return (e.scrollHeight > e.clientHeight);
    }

    /** Determines if the table's container can scroll horizontally */
    private canScrollX(): boolean {
        var e = this.table.parentElement;
        return (e.scrollWidth > e.clientWidth);
    }

    /** Matches the widths of the copy head columns to the original head columns */
    private matchColumnWidths(): void {
        var cols = this.head.getElementsByTagName("th");
        var copies = this.headCopy.getElementsByTagName("th");


        for (var i = 0; i < cols.length; i++) {
            let col = cols[i];
            let copy = copies[i];

            copy.style.width = `${col.offsetWidth}px`;

            /*copy.style.boxSizing = "content-box";
            copy.style.position = 'absolute';
            copy.style.display = "block";
            copy.style.top = "0";
            copy.style.left = Math.max(0, col.offsetLeft - 1) + "px";*/
        }
    }

    /**
     * Gets the x and y offsets of an element relative to the entire document
     * @param elem The HTML element to get the offset for
     */
    private getDocumentRelativeOffset(elem: HTMLElement): IOffset {
        var result: IOffset = {
            x: elem.offsetLeft,
            y: elem.offsetTop
        }

        var parent = elem.offsetParent as HTMLElement;
        while (parent) {
            result.x += parent.offsetLeft;
            result.y += parent.offsetTop;
            parent = parent.offsetParent as HTMLElement;
        }

        return result;
    }

    /**
     * Gets the x and y offsets of an element relative to the viewport (ie visible scrolled portion)
     * @param elem The HTML element to get the offset for
     */
    private getViewportRelativeOffset(elem: HTMLElement): IOffset {
        var offset = this.getDocumentRelativeOffset(elem);

        if (document.documentElement && (document.documentElement.scrollTop || document.documentElement.scrollLeft)) {
            offset.x -= document.documentElement.scrollLeft;
            offset.y -= document.documentElement.scrollTop;
        }
        else if (document.body && (document.body.scrollTop || document.body.scrollLeft)) {
            offset.x -= document.body.scrollLeft;
            offset.y -= document.body.scrollTop;
        }
        else if (window.pageXOffset || window.pageYOffset) {
            offset.x -= window.pageXOffset;
            offset.y -= window.pageYOffset;
        }

        return offset;
    }
}


//No jquery is used, so we can invoke this right away
TableFreezePaneUtility.enableFreezePanes("table-freeze-panes");


$(function () {
    $('.report-print-link').click(function (e) {
        e.preventDefault();
        var w = window.open($(this).attr('href'),
            'Print Report',
            'width=800,height=600');
        w.focus();
        w.print();
    });
});